import { 
   activateChannelSuccess,
   deactivateChannelSuccess,
   deleteChannelSuccess,
   fetchChannelsFinished, 
   fetchChannelsStarted, 
   fetchChannelsSuccess, 
   fetchWhatsappQrFinished, 
   fetchWhatsappQrStarted, 
   fetchWhatsappQrSuccess,
   fetchMaxQrFinished,
   fetchMaxQrStarted,
   fetchMaxQrSuccess,
   setMaxScanStarted,
   setWhatsappScanStarted
} from "."
import ChannelsService from "../../../services/channelsService"

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const createFetchQrThunk = ({
   getQrState,
   fetchQrStartedAction,
   fetchQrSuccessAction,
   fetchQrFinishedAction,
   createChannelBody,
   onSuccess
}) => () => async (dispatch, getState) => {
   const { qrLoading, qrCode, sourceId } = getQrState(getState().channels)

   if (qrLoading || (qrCode && sourceId)) {
      return
   }

   try {
      dispatch(fetchQrStartedAction())
      const data = await ChannelsService.createChannel(createChannelBody)
      dispatch(fetchQrSuccessAction(data))
      if (onSuccess) onSuccess({ data, dispatch })
   } catch (err) {
      console.log(err)
   } finally {
      dispatch(fetchQrFinishedAction())
   }
}

const createStartScanThunk = ({
   getScanState,
   setScanStartedAction,
   retryDelays = [0],
   retryStatuses = []
}) => (body) => async (dispatch, getState) => {
   const { isScanStarted, sourceId } = getScanState(getState().channels)

   if (isScanStarted && body?.sourceId && body.sourceId === sourceId) {
      return true
   }

   try {
      for (let attempt = 0; attempt < retryDelays.length; attempt++) {
         if (retryDelays[attempt]) {
            await sleep(retryDelays[attempt])
         }

         try {
            const data = await ChannelsService.setChannel(body)
            if (data.status === 'Начат процесс считывания QR кода') {
               dispatch(setScanStartedAction(true))
               return true
            }
         } catch (err) {
            const status = err?.response?.status
            const isRetryableError = retryStatuses.includes(status)
            const isLastAttempt = attempt === retryDelays.length - 1

            if (!isRetryableError || isLastAttempt) {
               throw err
            }
         }
      }
   } catch (err) {
      console.log(err)
   }
}

export const fetchChannels = () => async (dispatch) => {
   try {
      dispatch(fetchChannelsStarted())
      const data = await ChannelsService.fetchChannels()
      dispatch(fetchChannelsSuccess(data))
   } catch (err) {
      console.log(err)
   } finally {
      dispatch(fetchChannelsFinished())
   }
}

export const fetchWhatsappQR = createFetchQrThunk({
   getQrState: (channelsState) => ({
      qrLoading: channelsState.qrLoading,
      qrCode: channelsState.qrCode,
      sourceId: channelsState.sourceId
   }),
   fetchQrStartedAction: fetchWhatsappQrStarted,
   fetchQrSuccessAction: fetchWhatsappQrSuccess,
   fetchQrFinishedAction: fetchWhatsappQrFinished,
   createChannelBody: { channel: 'whatsapp' }
})

export const createWhatsappChannel = createStartScanThunk({
   getScanState: (channelsState) => ({
      isScanStarted: channelsState.isWhatsappScanStarted,
      sourceId: channelsState.sourceId
   }),
   setScanStartedAction: setWhatsappScanStarted
})

export const fetchMaxQR = createFetchQrThunk({
   getQrState: (channelsState) => ({
      qrLoading: channelsState.maxQrLoading,
      qrCode: channelsState.maxQrCode,
      sourceId: channelsState.maxSourceId
   }),
   fetchQrStartedAction: fetchMaxQrStarted,
   fetchQrSuccessAction: fetchMaxQrSuccess,
   fetchQrFinishedAction: fetchMaxQrFinished,
   createChannelBody: { channel: 'max', name: 'max' },
   onSuccess: ({ data, dispatch }) => {
      if (data?.sourceId && !data?.qrCode && !data?.qrLink) {
         dispatch(setMaxScanStarted(true))
      }
   }
})

export const createMaxChannel = createStartScanThunk({
   getScanState: (channelsState) => ({
      isScanStarted: channelsState.isMaxScanStarted,
      sourceId: channelsState.maxSourceId
   }),
   setScanStartedAction: setMaxScanStarted,
   retryDelays: [0, 1500, 3000],
   retryStatuses: [502, 504]
})

export const createChannel = (body) => async (dispatch) => {
   try {
      const data = await ChannelsService.createChannel(body)

      if (data?.sourceId) return data.sourceId
   } catch (err) {
      if (err?.response?.data?.message?.data?.validation?.phone?.length) {
         return Promise.reject(err?.response?.data?.message?.data?.validation?.phone[0])
      } else if (err?.response?.data?.message?.data?.validation?.code?.length) {
         return Promise.reject('')
      } else if (err?.response?.data?.message?.data?.validation?.account_id?.length) {
         return Promise.reject(err?.response?.data?.message?.data?.validation?.account_id[0])
      } else {
         return Promise.reject()
      }
   }
}

export const createChannelWithCode = (body) => async (dispatch) => {
   try {
      const data = await ChannelsService.setChannel(body)

      if (data.message !== 'Канал установлен') return Promise.reject()
   } catch (err) {
      if (err?.response?.data?.message?.data?.validation?.account_id?.length) {
         return Promise.reject(err?.response?.data?.message?.data?.validation?.account_id[0])
      } else if (err?.response?.data?.message?.data?.validation?.code?.length) {
         return Promise.reject(err?.response?.data?.message?.data?.validation?.code[0])
      } else {
         return Promise.reject('Не удалось подключить канал!')
      }
   }
}

export const createChannelWithPassword = (body) => async (dispatch) => {
   try {
      await ChannelsService.setChannelWithPassword(body)
   } catch (err) {
      if (err?.response?.data?.message?.data?.validation?.twoFactorPassword?.length) {
         return Promise.reject(err?.response?.data?.message?.data?.validation?.twoFactorPassword[0])
      } else {
         return Promise.reject('Не удалось подключить канал!')
      }
   }
}

export const activateChannel = ({ id, phone, channel }) => async (dispatch) => {
   try {
      const data = await ChannelsService.activateChannel({ sourceId: id, phone, channel })
      if (data.sourceId) {
         alert('Не удалось активировать канал. Вам был выслан код подтверждения')
         return Promise.reject(data)
      }

      dispatch(activateChannelSuccess(id))
   } catch (err) {
      console.log(err)
   }
}

export const activateChannelWithData = (body) => async (dispatch) => {
   try {
      const data = await ChannelsService.setChannel(body)
      if (data.message !== 'Канал установлен') return Promise.reject()

      dispatch(activateChannelSuccess(body.sourceId))
   } catch (err) {
      console.log(err)
   }
}

export const deactivateChannel = ({ id }) => async (dispatch) => {
   try {
      await ChannelsService.deactivateChannel(id)
      dispatch(deactivateChannelSuccess(id))
   } catch (err) {
      console.log(err)
   }
}

export const deleteChannel = (id) => async (dispatch) => {
   try {
      await ChannelsService.deleteChannel(id)
      dispatch(deleteChannelSuccess(id))
   } catch (err) {
      console.log(err)
   }
}